/**
 * usePlanCuration — Capa de curación del plan estructurado, ahora PERSISTIDA.
 *
 * Rediseño NU: la lista mostrada nace del DOCUMENTO ÚNICO (`liveSoap.fhir`,
 * con ids estables del servidor). Este hook mantiene un overlay OPTIMISTA
 * (respuesta inmediata al borrar/editar/agregar) y persiste cada mutación vía
 * `portal_editarNota` (fhirAcciones). Cuando el snapshot del documento
 * refleja el cambio, el overlay se vuelve un no-op:
 *   - `removed`: ids que ya no están en la base → filtro sin efecto.
 *   - `edits`: parche igual al valor de la base → sin efecto.
 *   - `added`: el servidor acepta el id propuesto por el cliente → la base lo
 *     trae y el overlay lo omite (present check).
 *
 * Los cambios del ASISTENTE ya no pasan por aquí: portal_asistenteMedico los
 * escribe server-side y llegan por el snapshot.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FHIRItem } from '../services/medicalAI';

export interface MedPatch {
  display?: string;
  presentation?: string;
  dose?: string;
  route?: string;
  frequency?: string;
  duration?: string;
}

/** Acción FHIR tal como la espera portal_editarNota (merge canónico). */
export interface FhirAccionCliente extends Partial<FHIRItem> {
  action: 'Add' | 'Modify' | 'Remove';
  id?: string;
}

interface Curation {
  removed: Set<string>;
  edits: Record<string, MedPatch>;
  added: FHIRItem[];
  educacion: string | null;
}

const EMPTY: Curation = { removed: new Set(), edits: {}, added: [], educacion: null };

export interface PlanRemoteOptions {
  /** Persiste acciones FHIR en el documento único (CF editarNota). */
  persistir?: (acciones: FhirAccionCliente[]) => void;
  /** Persiste el override de educación (la página puede debouncearlo). */
  persistirEducacion?: (texto: string) => void;
}

export function usePlanCuration(
  baseFhir: FHIRItem[],
  baseEducation: string,
  remote?: PlanRemoteOptions,
) {
  const [cur, setCur] = useState<Curation>(EMPTY);
  const remoteRef = useRef(remote);
  useEffect(() => {
    remoteRef.current = remote;
  }, [remote]);

  // Ids de `added` ya enviados al servidor (un add manual nace con display
  // vacío y solo se persiste cuando el médico escribe el nombre).
  const persistedAddsRef = useRef<Set<string>>(new Set());

  /** Lista final mostrada = base − removidos, con edits, + agregados. */
  const fhir = useMemo<FHIRItem[]>(() => {
    let out = baseFhir.filter((i) => !cur.removed.has(i.id));
    if (Object.keys(cur.edits).length > 0) {
      out = out.map((i) => (cur.edits[i.id] ? { ...i, ...cur.edits[i.id] } : i));
    }
    const present = new Set(out.map((i) => i.id));
    for (const a of cur.added) {
      if (cur.removed.has(a.id) || present.has(a.id)) continue;
      out.push(a);
    }
    return out;
  }, [baseFhir, cur]);

  const healthEducation = cur.educacion ?? baseEducation;

  /** Marca un ítem como eliminado y lo persiste. */
  const removeItem = useCallback((id: string) => {
    let persistir = false;
    setCur((c) => {
      const removed = new Set(c.removed);
      removed.add(id);
      return { ...c, removed, added: c.added.filter((a) => a.id !== id) };
    });
    // Solo se persiste si el servidor conoce el ítem (base o add ya enviado).
    persistir = baseFhir.some((i) => i.id === id) || persistedAddsRef.current.has(id);
    if (persistir) remoteRef.current?.persistir?.([{ action: 'Remove', id }]);
  }, [baseFhir]);

  /** Edita campos de un ítem (existente en base → edits; agregado → en sitio). */
  const editItem = useCallback((id: string, patch: MedPatch) => {
    setCur((c) => {
      if (c.added.some((a) => a.id === id)) {
        const added = c.added.map((a) => (a.id === id ? { ...a, ...patch } : a));
        // Un add manual nace sin nombre: se persiste como Add COMPLETO en
        // cuanto tiene display; después, los cambios van como Modify.
        const item = added.find((a) => a.id === id)!;
        if (item.display && item.display.trim()) {
          if (!persistedAddsRef.current.has(id)) {
            persistedAddsRef.current.add(id);
            remoteRef.current?.persistir?.([{ ...item, action: 'Add' }]);
          } else {
            remoteRef.current?.persistir?.([{ action: 'Modify', id, type: item.type, display: item.display, ...patch }]);
          }
        }
        return { ...c, added };
      }
      const base = baseFhir.find((i) => i.id === id);
      if (base) {
        remoteRef.current?.persistir?.([{ action: 'Modify', id, type: base.type, ...patch }]);
      }
      return { ...c, edits: { ...c.edits, [id]: { ...c.edits[id], ...patch } } };
    });
  }, [baseFhir]);

  /** Agrega un ítem a mano. Se persiste cuando tenga nombre (ver editItem). */
  const addItem = useCallback((item: FHIRItem) => {
    setCur((c) => {
      const removed = new Set(c.removed);
      removed.delete(item.id);
      return { ...c, added: [...c.added, item], removed };
    });
    if (item.display && item.display.trim()) {
      persistedAddsRef.current.add(item.id);
      remoteRef.current?.persistir?.([{ ...item, action: 'Add' }]);
    }
  }, []);

  const setEducacion = useCallback((text: string) => {
    setCur((c) => ({ ...c, educacion: text }));
    remoteRef.current?.persistirEducacion?.(text);
  }, []);

  return { fhir, healthEducation, removeItem, editItem, addItem, setEducacion };
}
